/**
 * `/setup` — first-time captain onboarding form (web UI Phase 2 chunk 4/6).
 *
 * Captures the two inputs needed to seed a season: WTTA schedule URL +
 * roster CSV. POSTs to `/api/tool/import_season`, which doesn't exist
 * yet (chunk 7 wires the HTTP→MCP gateway). A 404 from that gateway is
 * EXPECTED here and surfaced as a "not yet available" banner so chunk-4
 * acceptance testing can still walk the full form path.
 *
 * The form prefers a file upload over a textarea paste when both are
 * populated, matching how the founder framing reads ("attach your roster
 * CSV — or paste it"). One canonical roster_csv string lands in the
 * payload either way.
 *
 * Hard scope: no ISD code collection (chunk 5 owns the /setup/isd
 * follow-up form), no season view nav past the success state (chunk 6
 * wires the read view). On success we Navigate to `/setup/isd` —
 * which is where the captain attaches each player to their school
 * districts. Chunk 5 owns that page.
 */

import { useState, type ChangeEvent, type FormEvent } from "react";
import { Navigate } from "react-router-dom";
import { ApiError, apiPost } from "../lib/api";
import { useAuthedSession } from "../components/RequireAuth";

type ImportResult = {
  ok: true;
  team_id: string;
  matches_imported?: number;
  players_imported?: number;
};

type SubmitState =
  | { kind: "idle" }
  | { kind: "loading" }
  | { kind: "gateway_missing" }
  | { kind: "ok"; result: ImportResult }
  | { kind: "error"; message: string };

export default function Setup() {
  const session = useAuthedSession();

  const [scheduleUrl, setScheduleUrl] = useState("");
  const [rosterPaste, setRosterPaste] = useState("");
  const [rosterFileName, setRosterFileName] = useState<string | null>(null);
  const [rosterFileText, setRosterFileText] = useState<string | null>(null);
  const [state, setState] = useState<SubmitState>({ kind: "idle" });

  function onRosterFile(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) {
      setRosterFileName(null);
      setRosterFileText(null);
      return;
    }
    setRosterFileName(file.name);
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result;
      if (typeof result === "string") {
        setRosterFileText(result);
      }
    };
    reader.readAsText(file);
  }

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const url = scheduleUrl.trim();
    if (url.length === 0) {
      setState({
        kind: "error",
        message: "Paste your WTTA schedule URL.",
      });
      return;
    }
    // Prefer the file when both are populated.
    const roster = (rosterFileText ?? rosterPaste).trim();
    if (roster.length === 0) {
      setState({
        kind: "error",
        message: "Upload a roster CSV or paste it into the box below.",
      });
      return;
    }

    setState({ kind: "loading" });
    try {
      const result = await apiPost<ImportResult>("/api/tool/import_season", {
        team_id: session.team_id,
        schedule_url: url,
        roster_csv: roster,
      });
      setState({ kind: "ok", result });
    } catch (e) {
      if (e instanceof ApiError && e.status === 404) {
        // The HTTP gateway lands in chunk 7. This is expected, not a bug.
        setState({ kind: "gateway_missing" });
        return;
      }
      const msg =
        e instanceof ApiError
          ? `Import failed (${e.status}). ${
              typeof e.message === "string" ? e.message : ""
            }`.trim()
          : "Network error. Check your connection and retry.";
      setState({ kind: "error", message: msg });
    }
  }

  if (state.kind === "ok") {
    // After a successful roster import, hop to /setup/isd so the captain
    // attaches each player to their school district(s). Chunk 5 owns that
    // page. We intentionally don't go to /season yet — ISD coverage is a
    // prerequisite for school-conflict detection, which the season view
    // surfaces.
    return <Navigate to="/setup/isd" replace />;
  }

  return (
    <section>
      <h1>Set up your team</h1>
      <p className="muted">
        Signed in as captain <code>{session.captain_id.slice(0, 8)}…</code> for
        team <code>{session.team_id.slice(0, 8)}…</code> ({session.role}).
      </p>

      <form onSubmit={onSubmit} noValidate>
        <fieldset
          disabled={state.kind === "loading"}
          style={{ border: "none", padding: 0, margin: "1.5rem 0" }}
        >
          <h2>Schedule URL</h2>
          <p>
            <label htmlFor="schedule_url">
              Paste your WTTA schedule URL
            </label>
          </p>
          <p>
            <input
              id="schedule_url"
              name="schedule_url"
              type="url"
              placeholder="https://wtta.org/schedule/..."
              value={scheduleUrl}
              onChange={(e) => setScheduleUrl(e.target.value)}
              style={{ width: "100%", maxWidth: 560, padding: "0.5rem" }}
            />
          </p>
        </fieldset>

        <fieldset
          disabled={state.kind === "loading"}
          style={{ border: "none", padding: 0, margin: "1.5rem 0" }}
        >
          <h2>Roster</h2>
          <p>Upload a CSV file, or paste the CSV body below.</p>
          <p>
            <input
              id="roster_file"
              name="roster_file"
              type="file"
              accept=".csv,text/csv"
              onChange={onRosterFile}
            />
            {rosterFileName && (
              <span className="muted" style={{ marginLeft: "0.5rem" }}>
                {rosterFileName}
              </span>
            )}
          </p>
          <p>
            <label htmlFor="roster_paste">Or paste CSV</label>
          </p>
          <p>
            <textarea
              id="roster_paste"
              name="roster_paste"
              rows={8}
              placeholder="name,phone,usta_id,ntrp,flight&#10;Amy Baer,512-555-1234,1234567890,4.0,Flight 1 Green"
              value={rosterPaste}
              onChange={(e) => setRosterPaste(e.target.value)}
              style={{
                width: "100%",
                maxWidth: 720,
                padding: "0.5rem",
                fontFamily: "ui-monospace, monospace",
              }}
            />
          </p>
          {rosterFileName && rosterPaste.trim().length > 0 && (
            <p className="muted">
              <small>
                Both populated — the uploaded file <code>{rosterFileName}</code>{" "}
                will be used. Clear it to use the pasted CSV instead.
              </small>
            </p>
          )}
        </fieldset>

        <p>
          <button
            type="submit"
            className="button"
            disabled={state.kind === "loading"}
          >
            {state.kind === "loading" ? "Importing…" : "Import season"}
          </button>
        </p>

        {state.kind === "gateway_missing" && (
          <p role="alert" className="muted">
            Import not yet available — chunk 7 wires this. (Phase 2 in
            progress.)
          </p>
        )}
        {state.kind === "error" && (
          <p role="alert" style={{ color: "#b3261e" }}>
            {state.message}
          </p>
        )}
      </form>
    </section>
  );
}
